import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import BanModal from './BanModal';
import UnbanModal from './UnbanModal';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import Modal from 'react-modal';
import Loading from '../components/Loading';
import GuestFormProps from '../interfaces/GuestFormProps';
import ParamTypes from '../interfaces/ParamTypes';
import GlobalContextI from '../interfaces/GlobalContext';
import GlobalContext from '../contexts/GlobalContext';
import AllAccessGuestI from '../interfaces/AllAccessGuest';
import GuestI from '../interfaces/Guest';
import getAgreement from '../functions/getAgreement';
import EntryDates from '../components/EntryDates';
import { SubmitHandler, useForm } from 'react-hook-form';
import updateGuest from '../functions/updateGuest';
import getGuestID from '../functions/getGuestID';
import goBack from '../functions/goBack';
import { parseDob, fmtIso } from '../functions/dob';
import validator from 'validator';
import useDiscount from '../hooks/useDiscount';
import Form from '../components/Form';
import Button from '../components/Button';

/**
 * Collapsible accordion section for the guest edit form. Uses
 * `display: none` on collapse rather than unmounting children so RHF
 * state, defaultValues, and validation registrations stay intact when
 * the section is collapsed.
 */
const Section = ({
    title,
    defaultOpen = false,
    children,
}: {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}) => {
    const [open, setOpen] = useState<boolean>(defaultOpen);
    return (
        <div
            style={{
                marginBottom: 14,
                border: '1px solid #444',
                borderRadius: 10,
                overflow: 'hidden',
            }}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    setOpen(!open);
                }}
                style={{
                    width: '100%',
                    padding: '14px 18px',
                    background: '#2c3e50',
                    color: '#fff',
                    border: 'none',
                    fontSize: 17,
                    fontWeight: 700,
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <span>{title}</span>
                <span style={{ fontSize: 14, opacity: 0.85 }}>
                    {open ? '▾ Collapse' : '▸ Expand'}
                </span>
            </button>
            <div
                style={{
                    display: open ? 'block' : 'none',
                    padding: '12px 14px',
                    background: '#1f1f1f',
                }}
            >
                {children}
            </div>
        </div>
    );
};

const GuestForm = ({ action }: GuestFormProps) => {
    const { user, guest, setGuest } = useContext<GlobalContextI>(GlobalContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [isNotModified, setIsNotModified] = useState<boolean>(true);
    const [standingReason, setStandingReason] = useState<string>('');
    const [pendingStandingBad, setPendingStandingBad] = useState<boolean>(false);
    const [showBanModal, setShowBanModal] = useState<boolean>(false);
    const [banRecord, setBanRecord] = useState<any>(null);
    const [showUnbanModal, setShowUnbanModal] = useState<boolean>(false);
    const [showStandingGoodModal, setShowStandingGoodModal] = useState<boolean>(false);
    const [pendingStandingGood, setPendingStandingGood] = useState<boolean>(false);
    const [showStandingModal, setShowStandingModal] = useState<boolean>(false);
    const { discount, loadingDiscount } = useDiscount(
        action,
        guest?.points || 0
    );
    const history = useHistory();
    const { type }: ParamTypes = useParams();
    const {
        register,
        formState: { errors },
        handleSubmit,
        getValues,
        setValue,
        watch,
    } = useForm<GuestI | AllAccessGuestI>();
    // Live value drives the conditional pricing fields. getValues()
    // is a one-shot snapshot and does NOT trigger re-renders, so the
    // fields would never appear when the dropdown changed.
    const watchedPricingType =
        (watch('pricingType') as any) ||
        (guest as any)?.pricingType ||
        'regular';

    const handleGuest: SubmitHandler<GuestI | AllAccessGuestI> = async (
        data
    ) => {
        if (action === 'add') {
            setGuest({
                ...data,
                guestID: getGuestID({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    licenseID: data.licenseID,
                }).toLowerCase(),
                type: type,
            });

            return history.push(`/guests/notice`);
        }

        if (action === 'edit') {
            if (guest.type === 'allAccess') {
                data.active = data.active === 'active';
            }

            const effectivelyBad =
                (banRecord && banRecord.status === 'active') ||
                (guest?.standing || '').toUpperCase() === 'BAD';
            const nowBad = (data.standing || '').toUpperCase() === 'BAD';

            // Append standingHistory + attach standingReason to the
            // PATCH body for both directions (BAD↔GOOD). The server's
            // PATCH /api/guests/:id coupling reads standingReason and
            // calls bansService.createOrReactivate / deactivate.
            if (nowBad !== effectivelyBad && (data.standing === 'BAD' || data.standing === 'GOOD')) {
                const prior = (guest as any)?.standingHistory || [];
                (data as any).standingHistory = [
                    ...prior,
                    {
                        standing: data.standing,
                        reason: standingReason || '(no reason given)',
                        changedAt: new Date(),
                    },
                ];
                (data as any).standingReason = standingReason || '';
            }

            await updateGuest(guest, data, setLoading, history);
            // refresh local ban state so a re-render shows the
            // updated banner (or clears it).
            await refreshBanState();
            return;
        }
    };

    const handleStandingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = (e.target.value || '').toUpperCase();
        // Effective current state: ban record drives this when an
        // active ban exists (server's source of truth), falling back
        // to the stored guest.standing for legacy rows without bans.
        const effectivelyBad =
            (banRecord && banRecord.status === 'active') ||
            (guest?.standing || '').toUpperCase() === 'BAD';

        if (value === 'BAD' && !effectivelyBad) {
            setPendingStandingBad(true);
            setPendingStandingGood(false);
            setShowStandingModal(true);
        } else if (value === 'GOOD' && effectivelyBad) {
            // BAD → GOOD: requires unban reason. Server-side coupling
            // will translate this to BansService.deactivate when the
            // form submits.
            setPendingStandingGood(true);
            setPendingStandingBad(false);
            setShowStandingGoodModal(true);
        } else {
            setPendingStandingBad(false);
            setPendingStandingGood(false);
            setStandingReason('');
        }
    };

    const handleAgreement = async (e: any) => {
        const id = e.target.value;

        if (id === 'default') {
            return;
        }

        return await getAgreement(guest, user, setLoading, parseInt(id, 10));
    };

    const handleGoBack = (e: any) => {
        e.preventDefault();

        return goBack(guest, getValues(), setIsNotModified, history);
    };

    // ── Phase 3: fetch ban state on edit ──
    // The bans collection is the source of truth for whether this
    // guest is currently blocked at the kiosk. We surface that as
    // a banner + standing-display override. The lookup uses the
    // guest's idHash directly (Phase 2 ensures every active row has
    // one).
    const refreshBanState = async () => {
        if (action !== 'edit') return;
        const idHash = (guest as any)?.idHash;
        if (!idHash) {
            setBanRecord(null);
            return;
        }
        try {
            const r = await axios.get(`/api/bans/lookup/${idHash}`);
            setBanRecord(r.data || null);
        } catch (e) {
            // 404 / not-found is acceptable — no ban exists.
            setBanRecord(null);
        }
    };
    useEffect(() => {
        refreshBanState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(guest as any)?.idHash, action]);

    // Once we know the guest is actively banned, force the standing
    // dropdown to BAD so it matches the ban state. Server-side this
    // is the source of truth; client renders to match.
    useEffect(() => {
        if (banRecord && banRecord.status === 'active') {
            try { setValue('standing', 'BAD'); } catch {}
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [banRecord]);

    if (loading || loadingDiscount) {
        return <Loading loading={loading} />;
    }

    return (
        <>
            {!isNotModified && (
                <div className="new-guest-container__text">
                    <p>
                        You have modified guest's profile please press continue
                        to save changes or continue without saving button.
                    </p>

                    <button
                        className="yellow-button margin-guest"
                        onClick={() => history.push(guest?.page as string)}
                    >
                        Continue without saving
                    </button>
                </div>
            )}

            {action === 'edit' && banRecord && banRecord.status === 'active' && (
                <div
                    style={{
                        background: '#3a1010',
                        border: '2px solid #ff2d2d',
                        borderRadius: 8,
                        padding: '14px 18px',
                        marginBottom: 16,
                        color: '#fff',
                    }}
                >
                    <div
                        style={{
                            fontSize: '1.4rem',
                            fontWeight: 900,
                            color: '#ff5555',
                            letterSpacing: 2,
                            marginBottom: 6,
                        }}
                    >
                        ⛔ BANNED — DO NOT ADMIT
                    </div>
                    <div style={{ marginBottom: 4 }}>
                        <strong>Reason:</strong> {banRecord.reason || '(no reason logged)'}
                    </div>
                    {banRecord.bannedAt && (
                        <div style={{ fontSize: '0.85rem', color: '#ffaaaa', marginBottom: 8 }}>
                            Banned at: {new Date(banRecord.bannedAt).toLocaleString()}
                            {banRecord.bannedBy ? ' · by ' + String(banRecord.bannedBy).slice(-8) : ''}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowUnbanModal(true);
                        }}
                        style={{
                            background: '#1a6a1a',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 18px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 700,
                            letterSpacing: 1,
                        }}
                    >
                        ✓ Unban Guest
                    </button>
                </div>
            )}
            <Form onSubmit={handleSubmit(handleGuest)}>
                <button className="green-button margin-guest">Continue</button>
                {action === 'edit' && (
                    <div>
                        <span className="form-span">Entries: </span>
                        <select
                            className="form-input"
                            onChange={handleAgreement}
                        >
                            <option value="default">
                                Choose entry to download a file
                            </option>
                            {guest?.entryDates && (
                                <EntryDates entryDates={guest?.entryDates} />
                            )}
                        </select>
                    </div>
                )}

                {/* ── Section 1: Guest Pricing ───────────────────────── */}
                {type === 'allAccess' && (
                    <Section title="Guest Pricing" defaultOpen={true}>
                        {action === 'edit' && (
                            <div>
                                <span className="form-span">
                                    *Account Status:{' '}
                                </span>
                                <select
                                    {...register('active')}
                                    className="form-input"
                                    defaultValue={
                                        guest.active ? 'active' : 'inactive'
                                    }
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        )}
                        <div>
                            <span className="form-span">*Loyalty Points: </span>
                            {errors.points?.type === 'maxLength' && (
                                <p className="landing-container__warning">
                                    This field is too long
                                </p>
                            )}
                            <input
                                {...register('points', {
                                    maxLength: 32,
                                    setValueAs: (value) => parseInt(value, 10),
                                })}
                                type="text"
                                className="form-input"
                                placeholder="enter points..."
                                defaultValue={guest?.points || '0'}
                            />
                        </div>
                        <div>
                            <span className="form-span">*Additional fee: </span>
                            {errors.discount?.type === 'maxLength' && (
                                <p className="landing-container__warning">
                                    This field is too long
                                </p>
                            )}
                            <input
                                {...register('discount', {
                                    maxLength: 32,
                                    setValueAs: (value) => parseInt(value, 10),
                                })}
                                type="text"
                                className="form-input"
                                placeholder="enter Discount..."
                                defaultValue={0}
                            />
                        </div>

                        <div>
                            <span className="form-span">
                                Guest Pricing Type:
                            </span>
                            <select
                                {...register('pricingType')}
                                className="select-input"
                                defaultValue={
                                    (guest as any)?.pricingType || 'regular'
                                }
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setValue('pricingType' as any, v);
                                    if (v === 'comp') {
                                        setValue(
                                            'specialPricing.baseFee' as any,
                                            0
                                        );
                                        setValue(
                                            'specialPricing.applyAfter7' as any,
                                            false
                                        );
                                        setValue(
                                            'specialPricing.applyAfter8' as any,
                                            false
                                        );
                                        setValue(
                                            'specialPricing.applyAfter9' as any,
                                            false
                                        );
                                        setValue(
                                            'specialPricing.applyAfter10' as any,
                                            false
                                        );
                                    }
                                }}
                            >
                                <option value="regular">
                                    Regular All Access
                                </option>
                                <option value="vip">VIP Guest</option>
                                <option value="special">Special Guest</option>
                                <option value="comp">Comp Guest</option>
                            </select>
                        </div>

                        {watchedPricingType === 'regular' ? (
                            <p
                                style={{
                                    fontSize: 13,
                                    color: '#bdc3c7',
                                    fontStyle: 'italic',
                                    margin: '6px 0 0',
                                }}
                            >
                                Uses regular points-based All Access pricing.
                            </p>
                        ) : (
                            (() => {
                                const sp =
                                    (guest as any)?.specialPricing || {};
                                const pt = watchedPricingType;
                                return (
                                    <>
                                        <div>
                                            <span className="form-span">
                                                Special Base Fee ($):
                                            </span>
                                            <input
                                                {...register(
                                                    'specialPricing.baseFee',
                                                    {
                                                        setValueAs: (v) => {
                                                            const n =
                                                                parseInt(
                                                                    String(v),
                                                                    10
                                                                );
                                                            return Number.isFinite(
                                                                n
                                                            ) && n >= 0
                                                                ? n
                                                                : 0;
                                                        },
                                                    }
                                                )}
                                                type="number"
                                                min={0}
                                                className="form-input"
                                                defaultValue={
                                                    typeof sp.baseFee ===
                                                    'number'
                                                        ? sp.baseFee
                                                        : pt === 'comp'
                                                        ? 0
                                                        : 0
                                                }
                                            />
                                        </div>
                                        {(
                                            [
                                                ['applyAfter7', 'After 7 PM'],
                                                ['applyAfter8', 'After 8 PM'],
                                                ['applyAfter9', 'After 9 PM'],
                                                [
                                                    'applyAfter10',
                                                    'After 10 PM',
                                                ],
                                            ] as const
                                        ).map(([key, label]) => {
                                            const stored = (sp as any)[key];
                                            const defaultVal =
                                                typeof stored === 'boolean'
                                                    ? stored
                                                    : pt === 'comp'
                                                    ? false
                                                    : true;
                                            return (
                                                <div key={key}>
                                                    <span className="form-span">
                                                        Apply {label}{' '}
                                                        Surcharge?
                                                    </span>
                                                    <select
                                                        {...register(
                                                            `specialPricing.${key}` as any,
                                                            {
                                                                setValueAs: (
                                                                    v
                                                                ) =>
                                                                    String(
                                                                        v
                                                                    ) ===
                                                                    'true',
                                                            }
                                                        )}
                                                        className="select-input"
                                                        defaultValue={String(
                                                            defaultVal
                                                        )}
                                                    >
                                                        <option value="true">
                                                            Yes
                                                        </option>
                                                        <option value="false">
                                                            No
                                                        </option>
                                                    </select>
                                                </div>
                                            );
                                        })}
                                    </>
                                );
                            })()
                        )}
                    </Section>
                )}

                {/* ── Section 2: Stage Name / Personal Info ──────────── */}
                <Section
                    title="Stage Name / Personal Info"
                    defaultOpen={true}
                >
                    {type === 'allAccess' && (
                        <div>
                            <span className="form-span">*Stage Name: </span>
                            {errors.stageName?.type === 'required' && (
                                <p className="landing-container__warning">
                                    This field is required
                                </p>
                            )}
                            {errors.stageName?.type === 'maxLength' && (
                                <p className="landing-container__warning">
                                    This field is too long
                                </p>
                            )}
                            <input
                                {...register('stageName', {
                                    required: true,
                                    maxLength: 64,
                                })}
                                type="text"
                                className="form-input"
                                placeholder="enter stage name..."
                                defaultValue={guest?.stageName || ''}
                            />
                        </div>
                    )}
                    <div>
                        <span className="form-span">First Name: </span>
                        {errors.firstName?.type === 'required' && (
                            <p className="landing-container__warning">
                                This field is required
                            </p>
                        )}
                        {errors.firstName?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('firstName', {
                                required: true,
                                maxLength: 64,
                            })}
                            type="text"
                            className="form-input"
                            placeholder="enter first name..."
                            defaultValue={guest?.firstName || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">Middle Name: </span>
                        {errors.middleName?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('middleName', { maxLength: 32 })}
                            type="text"
                            className="form-input"
                            placeholder="enter middle name..."
                            defaultValue={guest?.middleName}
                        />
                    </div>
                    <div>
                        <span className="form-span">Last Name: </span>
                        {errors.lastName?.type === 'required' && (
                            <p className="landing-container__warning">
                                This field is required
                            </p>
                        )}
                        {errors.lastName?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('lastName', {
                                required: true,
                                maxLength: 64,
                            })}
                            type="text"
                            className="form-input"
                            placeholder="enter last name..."
                            defaultValue={guest?.lastName || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">
                            Date of Birth(eg. MONTH-DAY-YEAR):{' '}
                        </span>
                        {errors.dateOfBirth?.type === 'required' && (
                            <p className="landing-container__warning">
                                This field is required
                            </p>
                        )}
                        {errors.dateOfBirth?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('dateOfBirth', {
                                required: true,
                                maxLength: 32,
                            })}
                            type="date"
                            className="form-input"
                            placeholder="YYYY-MM-DD"
                            defaultValue={fmtIso(
                                parseDob(guest?.dateOfBirth as string)
                            )}
                        />
                    </div>
                    <div>
                        <span className="form-span">Street Address: </span>
                        {errors.streetAddress?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('streetAddress', { maxLength: 32 })}
                            type="text"
                            className="form-input"
                            placeholder="enter street address..."
                            defaultValue={guest?.streetAddress || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">City: </span>
                        {errors.city?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('city', { maxLength: 32 })}
                            type="text"
                            className="form-input"
                            placeholder="enter city..."
                            defaultValue={guest?.city || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">State: </span>
                        {errors.state?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('state', { maxLength: 32 })}
                            type="text"
                            className="form-input"
                            placeholder="enter state..."
                            defaultValue={guest?.state}
                        />
                    </div>
                    <div>
                        <span className="form-span">Sex: </span>
                        {errors.sex?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <select
                            {...register('sex', { maxLength: 5 })}
                            className="form-input"
                            defaultValue={guest?.sex || 'M'}
                        >
                            <option value="M">M</option>
                            <option value="F">F</option>
                        </select>
                    </div>
                    <div>
                        <span className="form-span">Eyes: </span>
                        {errors.eyes?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('eyes', { maxLength: 32 })}
                            type="text"
                            className="form-input"
                            placeholder="enter eye color..."
                            defaultValue={guest?.eyes || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">Height: </span>
                        {errors.height?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('height', { maxLength: 3 })}
                            type="text"
                            className="form-input"
                            placeholder="enter height..."
                            defaultValue={guest?.height || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">Weight: </span>
                        {errors.weight?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('weight', { maxLength: 3 })}
                            type="text"
                            className="form-input"
                            placeholder="enter weight..."
                            defaultValue={guest?.weight || ''}
                        />
                    </div>
                </Section>

                {/* ── Section 3: ID & Loyalty Info ───────────────────── */}
                <Section title="ID & Loyalty Info" defaultOpen={false}>
                    <div>
                        <span className="form-span">License #: </span>
                        {errors.licenseID?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('licenseID', {
                                required: true,
                                maxLength: 64,
                            })}
                            type="text"
                            className="form-input"
                            placeholder="enter driver license number..."
                            defaultValue={guest?.licenseID || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">Expiration Date: </span>
                        {errors.licenseExpDate?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('licenseExpDate', { maxLength: 32 })}
                            type="text"
                            className="form-input"
                            placeholder="enter driver license expiration date..."
                            defaultValue={guest?.licenseExpDate || ''}
                        />
                    </div>
                    {action === 'edit' && (
                        <div>
                            <span className="form-span">
                                Loyalty Rewards Number:{' '}
                            </span>
                            <input
                                type="text"
                                className="form-input"
                                disabled
                                defaultValue={guest?.guestID || ''}
                            />
                        </div>
                    )}
                </Section>

                {/* ── Section 4: Contact Info ────────────────────────── */}
                <Section title="Contact Info" defaultOpen={false}>
                    <div>
                        <span className="form-span">Phone Number: </span>
                        {errors.phoneNumber?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        <input
                            {...register('phoneNumber', { maxLength: 12 })}
                            type="text"
                            className="form-input"
                            placeholder="enter phone number..."
                            defaultValue={guest?.phoneNumber || ''}
                        />
                    </div>
                    <div>
                        <span className="form-span">Email Address: </span>
                        {errors.email?.type === 'maxLength' && (
                            <p className="landing-container__warning">
                                This field is too long
                            </p>
                        )}
                        {errors.email?.type === 'validate' && (
                            <p className="landing-container__warning">
                                This field is not an email
                            </p>
                        )}
                        <input
                            {...register('email', {
                                maxLength: 55,
                                validate: (value) =>
                                    validator.isEmail(value!) || value === '',
                            })}
                            type="email"
                            className="form-input"
                            placeholder="enter email address..."
                            defaultValue={guest?.email || ''}
                        />
                    </div>
                </Section>

                {/* ── Section 5: Status & Agreements (edit only) ─────── */}
                {action === 'edit' && (
                    <Section
                        title="Status & Agreements"
                        defaultOpen={false}
                    >
                        <div>
                            <span className="form-span">Standing: </span>
                            {errors.standing?.type === 'maxLength' && (
                                <p className="landing-container__warning">
                                    This field is too long
                                </p>
                            )}
                            <select
                                {...(() => {
                                    const r = register('standing', {
                                        maxLength: 4,
                                    });
                                    return {
                                        ...r,
                                        onChange: (e: any) => {
                                            r.onChange(e);
                                            handleStandingChange(e);
                                        },
                                    };
                                })()}
                                className="form-input"
                                defaultValue={guest?.standing || 'GOOD'}
                            >
                                <option value="GOOD">GOOD</option>
                                <option value="BAD">BAD</option>
                            </select>
                            {pendingStandingBad && standingReason && (
                                <p
                                    className="transaction-container__text"
                                    style={{ marginTop: '0.5rem' }}
                                >
                                    <span className="red-span">
                                        Reason logged:
                                    </span>{' '}
                                    {standingReason}
                                </p>
                            )}
                        </div>
                        <div>
                            <span className="form-span">ToS: </span>
                            {errors.tos?.type === 'maxLength' && (
                                <p className="landing-container__warning">
                                    This field is too long
                                </p>
                            )}
                            <select
                                {...register('tos', {
                                    maxLength: 5,
                                    setValueAs: (value: string) =>
                                        JSON.parse(value),
                                })}
                                className="form-input"
                                defaultValue={guest?.tos ? 'true' : 'false'}
                            >
                                <option value="false">false</option>
                                <option value="true">true</option>
                            </select>
                        </div>
                        <div>
                            <span className="form-span">Lease: </span>
                            {errors.lease?.type === 'maxLength' && (
                                <p className="landing-container__warning">
                                    This field is too long
                                </p>
                            )}
                            <select
                                {...register('lease', {
                                    maxLength: 5,
                                    setValueAs: (value: string) =>
                                        JSON.parse(value),
                                })}
                                className="form-input"
                                defaultValue={guest?.lease ? 'true' : 'false'}
                            >
                                <option value="false">false</option>
                                <option value="true">true</option>
                            </select>
                        </div>
                    </Section>
                )}

                {/* ── Section 6: Scan Audit (edit only, debug) ──────── */}
                {action === 'edit' && (guest?.scanSource ||
                    guest?.rawScanData ||
                    guest?.rawAamvaData) && (
                    <Section title="Scan Audit (debug)" defaultOpen={false}>
                        {guest?.needsManualReview && (
                            <p
                                className="landing-container__warning"
                                style={{ fontWeight: 700 }}
                            >
                                ⚠ This scan was flagged for manual review.
                            </p>
                        )}
                        <div>
                            <span className="form-span">Scan source:</span>{' '}
                            <code>{guest?.scanSource || '(legacy)'}</code>
                        </div>
                        <div>
                            <span className="form-span">Document type:</span>{' '}
                            <code>{guest?.documentType || '(unknown)'}</code>
                        </div>
                        <div>
                            <span className="form-span">Issuer:</span>{' '}
                            <code>
                                {guest?.issuingStateOrCountry || '(unknown)'}
                            </code>
                        </div>
                        <div>
                            <span className="form-span">Parsed at:</span>{' '}
                            <code>
                                {guest?.parsedAt
                                    ? new Date(
                                          guest.parsedAt as any
                                      ).toLocaleString()
                                    : '(not recorded)'}
                            </code>
                        </div>
                        <div>
                            <span className="form-span">Parser version:</span>{' '}
                            <code>
                                {guest?.parserVersion || '(legacy)'}
                            </code>
                        </div>
                        {Array.isArray(guest?.parserWarnings) &&
                            guest.parserWarnings.length > 0 && (
                                <div>
                                    <span className="form-span">
                                        Parser warnings:
                                    </span>
                                    <ul style={{ marginTop: 6 }}>
                                        {guest.parserWarnings.map(
                                            (w: string, i: number) => (
                                                <li key={i}>{w}</li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            )}
                        <div>
                            <span className="form-span">Raw scan data:</span>
                            <textarea
                                readOnly
                                rows={6}
                                style={{
                                    width: '100%',
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    marginTop: 6,
                                }}
                                value={
                                    (guest?.rawScanData as string) ||
                                    (guest?.rawAamvaData as string) ||
                                    ''
                                }
                            />
                        </div>
                    </Section>
                )}

                {action === 'edit' && !guest?.purgedAt && (
                    <button
                        type="button"
                        className="red-button margin"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowBanModal(true);
                        }}
                        style={{ background: '#a31a1a', color: '#fff' }}
                    >
                        🚫 Ban this guest
                    </button>
                )}

                {action === 'edit' && (
                    <button
                        className="red-button margin"
                        onClick={handleGoBack}
                    >
                        Go back
                    </button>
                )}
            </Form>
            {showUnbanModal && banRecord && (
                <UnbanModal
                    targetLabel={
                        `${guest?.firstName || ''} ${guest?.lastName || ''}`.trim() ||
                        `last4=${(guest?.licenseID || '').slice(-4) || '----'}`
                    }
                    onCancel={() => setShowUnbanModal(false)}
                    onSubmit={async (reason: string) => {
                        try {
                            await axios.patch(`/api/bans/${banRecord._id}/deactivate`, {
                                reason,
                            });
                            setShowUnbanModal(false);
                            await refreshBanState();
                            try { setValue('standing', 'GOOD'); } catch {}
                            window.alert('Ban lifted. Guest may now check in.');
                        } catch (e: any) {
                            window.alert(
                                'Lift failed: ' + (e?.response?.data?.message || e?.message || 'unknown'),
                            );
                        }
                    }}
                />
            )}
            {showBanModal && (
                <BanModal
                    title={`Ban ${guest?.firstName || ''} ${guest?.lastName || ''}`.trim()}
                    onCancel={() => setShowBanModal(false)}
                    onSubmit={async ({ reason, notes }) => {
                        try {
                            await axios.post('/api/bans/from-guest', {
                                guestId: guest?.guestID,
                                type: guest?.type || 'limited',
                                reason,
                                notes,
                            });
                            setShowBanModal(false);
                            window.alert('Ban created. This guest is now blocked at check-in.');
                        } catch (e: any) {
                            window.alert(
                                'Ban failed: ' + (e?.response?.data?.message || e?.message || 'unknown'),
                            );
                        }
                    }}
                />
            )}

            <Modal
                isOpen={showStandingModal}
                onRequestClose={() => {}}
                ariaHideApp={false}
                className="modal"
            >
                <header className="modal__header">
                    <h1 className="modal__header--h1">
                        Why is{' '}
                        <span className="red-span">
                            {guest?.stageName || guest?.firstName || 'this guest'}
                        </span>{' '}
                        being marked BAD?
                    </h1>
                </header>
                <span className="form-span">Reason</span>
                <textarea
                    className="form-input"
                    rows={3}
                    value={standingReason}
                    onChange={(e) => setStandingReason(e.target.value)}
                    placeholder="Explain why…"
                    autoFocus
                />
                <Button
                    color={'green'}
                    margin={true}
                    onClick={() => {
                        if (!standingReason.trim()) return;
                        setShowStandingModal(false); setShowStandingGoodModal(false);
                    }}
                >
                    Save Reason
                </Button>
                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => {
                        setShowStandingModal(false); setShowStandingGoodModal(false);
                        setPendingStandingBad(false);
                        setStandingReason('');
                        const sel = document.querySelector<HTMLSelectElement>(
                            'select[name="standing"]'
                        );
                        if (sel) sel.value = (banRecord && banRecord.status === 'active') ? 'BAD' : (guest?.standing || 'GOOD');
                    }}
                >
                    Cancel
                </Button>
            </Modal>
        </>
    );
};

export default GuestForm;
